import dynamic from "next/dynamic";

import { useCallback, useEffect, useMemo, useState } from "react";

import Button from "@stadium/ui/src/components/button/button";

import debounce from "lodash/debounce";
import { useSelector } from "react-redux";
import useRouter from "@stadium/hooks/lib/use-custom-route";
import Loaders from "@stadium/ui/src/components/loaders/loaders";

import { usePreviewModeContext } from "@contexts/preview-mode-context";
import useShowNewHeader from "@hooks/use-show-new-header";

import { getCountry } from "@selectors/country";
import {
  getStoreCloneId,
  getStoreID,
  getStoreParentId,
  getStoreSyncedToES,
  getCommonElasticSearchStoreVariables,
  getIsBundleShop,
  getIsLockerShop,
  getIsLegacyLockerShop,
} from "@selectors/store";
import { getMenuAllChildren } from "@selectors/menu";
import { getPricelessProductFetchVariables } from "@selectors/product";
import { getAllMenuTaxons } from "@helpers/menu";
import { getCommonGlobalCountryVariables, getLockerShopESQueryVars } from "@helpers/store";

import useFetchBaseProductList from "@hooks/search/use-fetch-base-product-list";
import { useStoreContext } from "@components/store/provider";
import { PRODUCT_LISTING_PER_PAGE, RELEVANCE_SORT_ITEM } from "@constants/products/listing";
import SearchedProducts from "@components/shared/global-search/searched-products";
import SearchedBrand from "@components/shared/global-search/searched-brand";

import { INITIAL_PAGE } from "@components/store/products/listing/listing-provider";
import { getSearchItemsRoute } from "@constants/routes";

import defaultStyles from "./main.module.scss";

const NewProductSearch = dynamic(() =>
  import("@components/shared/global-new-search/global-product-search"),
);

const DEFAULT_CURRENT_PAGE = 1;
const ITEMS_PER_PAGE = 10;

const GlobalProductSearch = ({ styles = defaultStyles }) => {
  const router = useRouter();
  const { searchQuery } = router.query;

  const { storeBasePath, isGlobalCountry, isPreviewCatalogMode, onlyHasAllProductsMenu } =
    useStoreContext();
  const storeParentId = useSelector(getStoreParentId);
  const storeId = useSelector(getStoreID);
  const country = useSelector(getCountry);
  const syncedToEs = useSelector(getStoreSyncedToES);
  const storeCloneId = useSelector(getStoreCloneId);
  const allChildren = useSelector(getMenuAllChildren);
  const commonESVariables = useSelector(getCommonElasticSearchStoreVariables);
  const isBundleShop = useSelector(getIsBundleShop);
  const isLockerShop = useSelector(getIsLockerShop);
  const isLegacyLockerShop = useSelector(getIsLegacyLockerShop);
  const pricelessVariables = useSelector(getPricelessProductFetchVariables);

  const { getStoreUpdatedAt } = usePreviewModeContext();

  const [searchValue, setSearchValue] = useState(
    searchQuery ? decodeURIComponent(searchQuery) : "",
  );

  const { allMenuTaxonIds: taxonIds, anyMenuWithSwag } = useMemo(
    () => getAllMenuTaxons(allChildren),
    [allChildren, country],
  );

  const handleSearchChange = (event) => {
    const { value } = event.target;
    setSearchValue(value);
  };

  const commonGlobalCountryVariables = getCommonGlobalCountryVariables({
    country,
    isGlobalCountry,
    countryKeyAttr: "countryCode",
  });

  const queryVariables = useMemo(() => {
    const variables = {
      storeId: storeParentId,
      designedStoreIds: [storeId],
      taxonIds,
      page: DEFAULT_CURRENT_PAGE,
      perPage: ITEMS_PER_PAGE,
      searchQuery: searchValue,
      skipWarehouse: !syncedToEs && !!storeCloneId,
      sortBy: RELEVANCE_SORT_ITEM.id,
      withSwag: anyMenuWithSwag,
      storeUpdatedAt: getStoreUpdatedAt(),
      optimizedSearch: isLockerShop,
      includeProductVariants: isBundleShop,
      showVariantsAsProducts: true,
      ...(isBundleShop && { bulkAvailable: false, withOutOfStock: true, withSwag: true }),
      ...commonESVariables,
      ...commonGlobalCountryVariables,
      ...getLockerShopESQueryVars({
        isLockerShop: isLegacyLockerShop,
      }),
      ...pricelessVariables,
    };

    return variables;
  }, [
    isBundleShop,
    storeParentId,
    storeId,
    country,
    searchValue,
    taxonIds,
    commonESVariables,
    getStoreUpdatedAt,
    pricelessVariables,
  ]);

  useEffect(() => {
    if (!onlyHasAllProductsMenu && searchValue) fetchSearchResults(queryVariables);
  }, [searchValue, country, taxonIds, onlyHasAllProductsMenu]);

  const {
    fetchedProducts,
    getProductList,
    updatedAggregations: aggregations,
    hasDataFetched,
  } = useFetchBaseProductList();
  const searchedBrands = aggregations?.searchedByBrandName;
  const showBrandsSuggestion = searchedBrands?.length > 0;

  const fetchSearchResults = useCallback(
    debounce((variables) => {
      getProductList({
        variables,
      });
    }, 1000),
    [getProductList],
  );

  const redirectToResults = () => {
    if (!searchValue) return;
    const pathname = getSearchItemsRoute({
      storeBasePath,
      searchQuery: encodeURIComponent(searchValue),
    });
    if (searchValue === searchQuery) document.getElementById("global-search").blur();
    else {
      router.push({
        pathname,
        query: {
          page: INITIAL_PAGE,
          perPage: PRODUCT_LISTING_PER_PAGE,
          sortBy: RELEVANCE_SORT_ITEM.id,
        },
      });
    }
    setSearchValue("");
  };

  const handleEnterKeyPress = (event) => {
    if (event.keyCode === 13) redirectToResults();
  };

  const noDataView = () => <div className={styles.noData}>No Data Found.</div>;

  const renderViewAllProducts = () => {
    if (fetchedProducts.length < 2) return null;
    return (
      <div className={styles.viewAllBlock}>
        <Button text="View all results" onClick={redirectToResults} className={styles.viewAllBtn} />
      </div>
    );
  };

  const renderBrandsResults = () => {
    const searchedBrands = aggregations?.searchedByBrandName;
    if (searchedBrands?.length > 0)
      return (
        <>
          <div className={styles.searchTopHead}>Brands</div>
          {searchedBrands.map((brand) => (
            <SearchedBrand data={brand} key={brand.id} />
          ))}
        </>
      );
  };

  const renderProductList = () => {
    if (!searchValue) return null;
    if (!onlyHasAllProductsMenu && !hasDataFetched) return <Loaders.Spinner />;
    if (onlyHasAllProductsMenu || !fetchedProducts.length) return noDataView();
    const filteredProducts = showBrandsSuggestion ? fetchedProducts.slice(0, 3) : fetchedProducts;
    return (
      <>
        <div className={styles.searchTopHead}>Top Suggestions</div>
        {filteredProducts.map((product) => (
          <SearchedProducts data={product} key={product.id} setSearchValue={setSearchValue} />
        ))}
        {renderBrandsResults()}
        {renderViewAllProducts()}
      </>
    );
  };

  const renderGlobalSearch = () => {
    return (
      <div
        tabIndex={0}
        className={`${styles.searchBlock} ${isPreviewCatalogMode ? styles.catalogSearchBlock : ""}`}
      >
        <span className={styles.iconBack} />
        <div className={styles.searchInputGroup}>
          <label>
            <input
              id="global-search"
              type="text"
              value={searchValue}
              className={isPreviewCatalogMode ? styles.previewCatalogSearchBar : ""}
              onChange={handleSearchChange}
              placeholder="Search all products"
              onKeyUp={handleEnterKeyPress}
              onSubmit={redirectToResults}
            />{" "}
            <span className={`${styles.iconSearch} ${searchValue ? styles.hideIcon : ""}`} />
          </label>
          {searchValue && (
            <button className={styles.iconClose} onClick={() => setSearchValue("")} />
          )}
        </div>

        <div className={styles.searchDropdown}>{renderProductList()}</div>
      </div>
    );
  };

  return renderGlobalSearch();
};

const SearchBarComponent = (props) => {
  const showNewHeader = useShowNewHeader();

  return showNewHeader ? <NewProductSearch {...props} /> : <GlobalProductSearch {...props} />;
};

export default SearchBarComponent;
